import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './shared/contexts/AuthContext';
import { OrgProvider } from './shared/contexts/OrgContext';
import OrganizationLocationWatcher from './shared/components/OrganizationLocationWatcher';
import { 
  LoginPage, 
  ResetPasswordPage,
  HomePage,
  SummaryPage,
  HomepageSummaryPage,
  TeamSquadPage,
  ScoreApp, 
  ReplayApp, 
  ReportsApp, 
  CMSApp,
  ControlRoomApp,
  RosterApp,
  RecentPage, 
  DiscoverPage, 
  NotificationsPage,
  PlayersPage,
  CaptureApp
} from './apps';
import OrganizationSelectionScreen from './apps/homepage/pages/OrganizationSelectionScreen';
import ProtectedRoute from './shared/components/ProtectedRoute';
import AppProtectedRoute from './shared/components/AppProtectedRoute';
import StandaloneAppRedirect from './shared/components/StandaloneAppRedirect';
import CleanUrlRedirect from './shared/components/CleanUrlRedirect';

/**
 * Main App component with organization-based routing strategy
 * 
 * All applications now require organization context via app.kridah.com:
 * 
 * Organization-Required Apps:
 * - /o/:orgId → Homepage application (app.kridah.com/o/:orgId)
 * - /o/:orgId/score/* → Score app for specific organization
 * - /o/:orgId/replay/* → Replay app for specific organization  
 * - /o/:orgId/reports/* → Reports app for specific organization
 * - /o/:orgId/cms/* → CMS app for specific organization
 * - /o/:orgId/capture/* → Capture app for specific organization (placeholder)
 * 
 * Standalone routes redirect to organization selection or primary organization:
 * - /score → Redirects to organization selection or primary org
 * - /reports → Redirects to organization selection or primary org
 * - /replay → Redirects to organization selection or primary org
 * - /cms → Redirects to organization selection or primary org
 * 
 */
const App: React.FC = () => {
  return (
    <AuthProvider>
      <OrgProvider>
        <Router>
          <OrganizationLocationWatcher />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Organization Selection Screen */}
            <Route path="/select-organization" element={
              <ProtectedRoute>
                <OrganizationSelectionScreen />
              </ProtectedRoute>
            } />
            
            {/* Standalone route redirects - Active Apps */}
            <Route path="/score/*" element={
              <ProtectedRoute>
                <StandaloneAppRedirect appName="score" />
              </ProtectedRoute>
            } />
            
            <Route path="/replay/*" element={
              <ProtectedRoute>
                <StandaloneAppRedirect appName="replay" />
              </ProtectedRoute>
            } />
            
            <Route path="/reports/*" element={
              <ProtectedRoute>
                <StandaloneAppRedirect appName="reports" />
              </ProtectedRoute>
            } />
            
            <Route path="/cms/*" element={
              <ProtectedRoute>
                <StandaloneAppRedirect appName="cms" />
              </ProtectedRoute>
            } />
            
            <Route path="/control-room/*" element={
              <ProtectedRoute>
                <StandaloneAppRedirect appName="control-room" />
              </ProtectedRoute>
            } />
            
            <Route path="/capture/*" element={
              <ProtectedRoute>
                <StandaloneAppRedirect appName="capture" />
              </ProtectedRoute>
            } />
            
            {/* Organization-specific routes (home.kridah.com/o/:orgId) */}
            <Route path="/o/:orgId" element={
              <ProtectedRoute>
                <CleanUrlRedirect />
                <HomePage />
              </ProtectedRoute>
            } />
            
            {/* For you page route */}
            <Route path="/o/:orgId/for-you" element={
              <ProtectedRoute>
                <CleanUrlRedirect />
                <HomePage />
              </ProtectedRoute>
            } />
            
            {/* Summary page route */}
            <Route path="/o/:orgId/summary" element={
              <ProtectedRoute>
                <CleanUrlRedirect />
                <HomepageSummaryPage />
              </ProtectedRoute>
            } />
            
            {/* Recent page route */}
            <Route path="/o/:orgId/recent" element={
              <ProtectedRoute>
                <CleanUrlRedirect />
                <RecentPage />
              </ProtectedRoute>
            } />
            
            {/* Discover page route */}
            <Route path="/o/:orgId/discover" element={
              <ProtectedRoute>
                <CleanUrlRedirect />
                <DiscoverPage />
              </ProtectedRoute>
            } />
            
            {/* Notifications page route */}
            <Route path="/o/:orgId/notifications" element={
              <ProtectedRoute>
                <CleanUrlRedirect />
                <NotificationsPage />
              </ProtectedRoute>
            } />
            
                    {/* Organization-specific Score App routes */}
                    <Route path="/o/:orgId/score/*" element={
                      <AppProtectedRoute appName="score">
                        <ScoreApp />
                      </AppProtectedRoute>
                    } />
            
            
                    {/* Organization-specific Replay App routes */}
                    <Route path="/o/:orgId/replay/*" element={
                      <AppProtectedRoute appName="replay">
                        <ReplayApp />
                      </AppProtectedRoute>
                    } />
            
                    {/* Organization-specific Reports App routes */}
                    <Route path="/o/:orgId/reports/*" element={
                      <AppProtectedRoute appName="reports">
                        <ReportsApp />
                      </AppProtectedRoute>
                    } />
            
            
            
            
            
                    {/* Organization-specific CMS App routes */}
                    <Route path="/o/:orgId/cms/*" element={
                      <AppProtectedRoute appName="cms">
                        <CMSApp />
                      </AppProtectedRoute>
                    } />
                    
                    {/* Organization-specific Capture App routes */}
                    <Route path="/o/:orgId/capture/*" element={
                      <AppProtectedRoute appName="capture">
                        <CaptureApp />
                      </AppProtectedRoute>
                    } />
                    
                    {/* Organization-specific Control Room App routes */}
                    <Route path="/o/:orgId/control-room/*" element={
                      <AppProtectedRoute appName="control-room">
                        <ControlRoomApp />
                      </AppProtectedRoute>
                    } />

                    {/* Organization-specific Roster Summary route - DEPRECATED */}
                    {/* Direct URL access to roster summary is deprecated */}
                    <Route path="/o/:orgId/roster/summary" element={
                      <AppProtectedRoute appName="roster">
                        <SummaryPage />
                      </AppProtectedRoute>
                    } />

                    {/* Organization-specific Players route */}
                    <Route path="/o/:orgId/roster/players" element={
                      <AppProtectedRoute appName="roster">
                        <PlayersPage />
                      </AppProtectedRoute>
                    } />

                    {/* Organization-specific Team Squad route */}
                    <Route path="/o/:orgId/roster/:teamId" element={
                      <AppProtectedRoute appName="roster">
                        <TeamSquadPage />
                      </AppProtectedRoute>
                    } />


                    {/* Organization-specific Roster App routes */}
                    <Route path="/o/:orgId/roster/*" element={
                      <AppProtectedRoute appName="roster">
                        <RosterApp />
                      </AppProtectedRoute>
                    } />
            
            {/* Organization-specific Capture App routes - Placeholder */}
            {/* Route will be added when CaptureApp is properly exported */}
            
            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </OrgProvider>
    </AuthProvider>
  );
};

export default App;
